import { createApi } from '@reduxjs/toolkit/query/react'
import { hcPortalBaseQuery } from '@/features/baseQuery.ts'
import {
  AddRewardExerciseConfigRequest,
  BulkUpdateRewardExercisesRequest,
  DownloadInvestmentTeamRewardExerciseTemplateRequest,
  DownloadRewardExerciseInitTemplateRequest,
  DownloadRewardExerciseRoundRequest,
  DownloadRewardStatementTemplateRequest,
  GetRewardExerciseDetailsRequest,
  ImportRewardExerciseInitTemplateRequest,
  ImportRewardExerciseProposalsRequest,
  LockRewardExerciseRoundRequest,
  NotifyRewardExerciseRoundReviewersRequest,
  RemoveRewardExercisesRequest,
  SyncRewardExerciseDataRequest,
  UnlockRewardExerciseRoundRequest,
  UpdateRewardExerciseCountryConfigRequest,
  UpdateRewardExerciseConfigRequest,
} from '@/features/reward-exercise/reward-exercise-request.def.ts'
import { HcError } from '@/common/types/api.def.ts'
import { downloadExcel, downloadFile } from '@/utils/misc.ts'
import {
  NotifyRewardExerciseRoundReviewersResult,
  RewardExerciseConfigSummary,
  RewardExerciseDetails,
  RewardExerciseItem,
} from '@/features/reward-exercise/reward-exercise.def.ts'

/**
 * The args of a GetRewardExerciseDetails query. `onBehalfOf` is part of the cache key, so the details page and
 * every cache update after a mutation must build the args the same way.
 */
export const toDetailsQueryArgs = (year: number, onBehalfOf?: string): GetRewardExerciseDetailsRequest =>
  onBehalfOf ? { year, onBehalfOf } : { year }

export const rewardExerciseApi = createApi({
  reducerPath: 'rewardExercise',
  baseQuery: hcPortalBaseQuery(),
  tagTypes: ['RewardExercise'],
  endpoints: (builder) => ({
    downloadRewardExerciseInitTemplate: builder.mutation<void, DownloadRewardExerciseInitTemplateRequest>({
      query: (request) => ({
        url: '/RewardExercise/DownloadRewardExerciseInitTemplate',
        method: 'POST',
        cache: 'no-cache',
        body: request,
        responseHandler: async (response) => {
          if (!response.ok) {
            return await response.json() as HcError
          }
          const blob = await response.blob()
          downloadExcel(`reward_exercise_template_${request.year}`, blob)
        },
      }),
    }),
    downloadRewardStatementTemplate: builder.mutation<void, DownloadRewardStatementTemplateRequest>({
      query: (request) => ({
        url: '/RewardExercise/DownloadRewardStatementTemplate',
        method: 'POST',
        cache: 'no-cache',
        body: request,
        responseHandler: async (response) => {
          if (!response.ok) {
            return await response.json() as HcError
          }
          const blob = await response.blob()
          downloadExcel(`reward_statement_template_${request.year}`, blob)
        },
      }),
    }),
    downloadRewardExerciseRound: builder.mutation<void, DownloadRewardExerciseRoundRequest>({
      query: (request) => ({
        url: '/RewardExercise/DownloadRewardExerciseRound',
        method: 'POST',
        cache: 'no-cache',
        body: request,
        responseHandler: async (response) => {
          if (!response.ok) {
            return await response.json() as HcError
          }
          const blob = await response.blob()
          downloadExcel(`reward_exercise_${request.year}_round_${request.roundIndex + 1}`, blob)
        },
      }),
    }),
    downloadInvestmentTeamRewardExerciseTemplate: builder.mutation<void, DownloadInvestmentTeamRewardExerciseTemplateRequest>({
      query: (request) => ({
        url: '/RewardExercise/DownloadInvestmentTeamRewardExerciseTemplate',
        method: 'POST',
        cache: 'no-cache',
        body: request,
        responseHandler: async (response) => {
          if (!response.ok) {
            return await response.json() as HcError
          }
          const blob = await response.blob()
          downloadExcel(`reward_exercise_investment_template_${request.year}`, blob)
        },
      }),
    }),
    importRewardExerciseInitTemplate: builder.mutation<void, ImportRewardExerciseInitTemplateRequest>({
      query: (request) => {
        const formData = new FormData()
        formData.append('year', request.year.toString())
        formData.append('excelFile', request.excelFile)

        return {
          url: '/RewardExercise/ImportRewardExerciseInitTemplate',
          method: 'POST',
          cache: 'no-cache',
          body: formData,
        }
      },
      invalidatesTags: (_result, _error, arg) => [{ type: 'RewardExercise', id: arg.year }],
    }),
    importRewardExerciseProposals: builder.mutation<number, ImportRewardExerciseProposalsRequest>({
      query: (request) => {
        const formData = new FormData()
        formData.append('year', request.year.toString())
        formData.append('roundIndex', request.roundIndex.toString())
        formData.append('excelFile', request.excelFile)

        return {
          url: '/RewardExercise/ImportRewardExerciseProposals',
          method: 'POST',
          cache: 'no-cache',
          body: formData,
        }
      },
      invalidatesTags: (_result, _error, arg) => [{ type: 'RewardExercise', id: arg.year }],
    }),
    getRewardExerciseConfigs: builder.query<RewardExerciseConfigSummary[], void>({
      query: () => ({
        url: '/RewardExercise/GetRewardExerciseConfigs',
        method: 'GET',
        cache: 'no-cache',
      }),
      providesTags: [{ type: 'RewardExercise', id: 'CONFIG_LIST' }],
    }),
    getRewardExerciseDetails: builder.query<RewardExerciseDetails, GetRewardExerciseDetailsRequest>({
      query: (request) => ({
        url: '/RewardExercise/GetRewardExerciseDetails',
        method: 'GET',
        cache: 'no-cache',
        params: request,
      }),
      providesTags: (_result, _error, arg) => [{ type: 'RewardExercise', id: arg.year }],
    }),
    downloadRewardExerciseDetails: builder.mutation<void, GetRewardExerciseDetailsRequest>({
      query: (request) => ({
        url: '/RewardExercise/DownloadRewardExerciseDetails',
        method: 'GET',
        cache: 'no-cache',
        params: request,
        responseHandler: async (response) => {
          if (!response.ok) {
            return await response.json() as HcError
          }
          const blob = await response.blob()
          downloadFile('reward_exercise.xlsx', blob)
        },
      }),
    }),
    bulkUpdateRewardExercises: builder.mutation<RewardExerciseItem[], BulkUpdateRewardExercisesRequest>({
      query: (request) => ({
        url: '/RewardExercise/BulkUpdateRewardExercises',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      onQueryStarted: async (request, { dispatch, queryFulfilled }) => {
        const { data: updated } = await queryFulfilled
        dispatch(rewardExerciseApi.util.updateQueryData(
          'getRewardExerciseDetails',
          toDetailsQueryArgs(request.year, request.onBehalfOf),
          draft => {
            const updatedDict = updated.reduce((acc, item) => ({
              ...acc,
              [item.id]: item,
            }), {} as Record<number, RewardExerciseItem>)
            return { ...draft, data: draft.data.map(item => updatedDict[item.id] ? updatedDict[item.id] : item) }
          },
        ))
      },
    }),
    removeRewardExercises: builder.mutation<boolean, RemoveRewardExercisesRequest>({
      query: (request) => ({
        url: '/RewardExercise/RemoveRewardExercises',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      onQueryStarted: async (request, { dispatch }) => {
        dispatch(rewardExerciseApi.util.updateQueryData(
          'getRewardExerciseDetails',
          { year: request.year } as GetRewardExerciseDetailsRequest,
          draft => ({ ...draft, data: draft.data.filter(item => !request.ids.includes(item.id)) }),
        ))
      },
    }),
    bulkLockRewardExerciseRound: builder.mutation<boolean, LockRewardExerciseRoundRequest>({
      query: (request) => ({
        url: '/RewardExercise/BulkLockRewardExerciseRound',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, arg) => [{ type: 'RewardExercise', id: arg.year }],
    }),
    notifyRewardExerciseRoundReviewers: builder.mutation<
      NotifyRewardExerciseRoundReviewersResult,
      NotifyRewardExerciseRoundReviewersRequest
    >({
      query: (request) => ({
        url: '/RewardExercise/NotifyRewardExerciseRoundReviewers',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
    }),
    unlockRewardExerciseRound: builder.mutation<boolean, UnlockRewardExerciseRoundRequest>({
      query: (request) => ({
        url: '/RewardExercise/UnlockRewardExerciseRound',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      onQueryStarted: async (request, { dispatch }) => {
        dispatch(rewardExerciseApi.util.updateQueryData(
          'getRewardExerciseDetails',
          { year: request.year } as GetRewardExerciseDetailsRequest,
          draft => ({
            ...draft, data: draft.data.map(item => item.id === request.id ? {
              ...item,
              proposedRewards: item.proposedRewards.map((round, index) => index === request.index ? {
                ...round,
                disabledAt: null,
                disabled: false,
              } : round),
            } as RewardExerciseItem : item),
          }),
        ))
      },
    }),
    addRewardExerciseConfig: builder.mutation<void, AddRewardExerciseConfigRequest>({
      query: (request) => ({
        url: '/RewardExercise/AddRewardExerciseConfig',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'RewardExercise', id: arg.year },
        { type: 'RewardExercise', id: 'CONFIG_LIST' },
      ],
    }),
    updateRewardExerciseConfig: builder.mutation<boolean, UpdateRewardExerciseConfigRequest>({
      query: (request) => ({
        url: '/RewardExercise/UpdateRewardExerciseConfig',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'RewardExercise', id: arg.year },
        { type: 'RewardExercise', id: 'CONFIG_LIST' },
      ],
    }),
    updateRewardExerciseCountryConfig: builder.mutation<boolean, UpdateRewardExerciseCountryConfigRequest>({
      query: (request) => ({
        url: '/RewardExercise/UpdateRewardExerciseCountryConfig',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, arg) => [{ type: 'RewardExercise', id: arg.year }],
    }),
    syncRewardExerciseData: builder.mutation<boolean, SyncRewardExerciseDataRequest>({
      query: (request) => ({
        url: '/RewardExercise/SyncRewardExerciseData',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, arg) => [{ type: 'RewardExercise', id: arg.year }],
    }),
  }),
})

export const {
  useDownloadRewardExerciseInitTemplateMutation,
  useDownloadRewardExerciseRoundMutation,
  useDownloadInvestmentTeamRewardExerciseTemplateMutation,
  useImportRewardExerciseInitTemplateMutation,
  useImportRewardExerciseProposalsMutation,
  useGetRewardExerciseConfigsQuery,
  useGetRewardExerciseDetailsQuery,
  useDownloadRewardExerciseDetailsMutation,
  useAddRewardExerciseConfigMutation,
  useBulkUpdateRewardExercisesMutation,
  useRemoveRewardExercisesMutation,
  useBulkLockRewardExerciseRoundMutation,
  useNotifyRewardExerciseRoundReviewersMutation,
  useUnlockRewardExerciseRoundMutation,
  useUpdateRewardExerciseCountryConfigMutation,
  useUpdateRewardExerciseConfigMutation,
  useSyncRewardExerciseDataMutation,
  useDownloadRewardStatementTemplateMutation,
} = rewardExerciseApi